import React, { Suspense, lazy, type ComponentType } from 'react';
import { SignUp } from '@clerk/clerk-react';
import {
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import DashboardLayout from './components/layout/DashboardLayout';
import { useAuthContext } from './hooks/useAuth';
import { AuthenticatedRoute } from './components/auth/AuthenticatedRoute'; // Issue #1456
import { PageLoadingSpinner } from './components/common/PageLoadingSpinner';
import { RouteErrorBoundary } from './components/ErrorBoundary';
import { FeatureGate } from './components/plan/FeatureGate';
import logger from './utils/logger';
import { isBookingHost } from './utils/bookingHost';
import {
  BOOKING_NESTED_ROUTE_PATHS,
  type BookingNestedRoutePath,
} from './routes/bookingRoutes';
import { isRepeatEnabled, REPEAT_HOME_PATH } from './lib/repeatGate';
import { useSubscription } from './hooks/useSubscription';
import { useIsRepeatFirstOwner } from './hooks/repeat/useIsRepeatFirstOwner';

// Issue #2078: チャンクロード失敗時にリトライするラッパー
// デプロイ後のハッシュ不一致による ChunkLoadError を自動リカバリ
const CHUNK_RETRY_KEY = 'ripia_chunk_reload';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    for (let i = 0; i <= retries; i++) {
      try {
        return await factory();
      } catch (error) {
        if (i === retries) {
          // 最終リトライ失敗: 1回だけページリロードを試行
          const hasReloaded = sessionStorage.getItem(CHUNK_RETRY_KEY);
          if (!hasReloaded) {
            sessionStorage.setItem(CHUNK_RETRY_KEY, '1');
            logger.warn(
              '[lazyWithRetry] Chunk load failed after retries, reloading page',
              { error }
            );
            window.location.reload();
            // reload後は到達しないが型を満たすため
            return { default: (() => null) as unknown as T };
          }
          sessionStorage.removeItem(CHUNK_RETRY_KEY);
          throw error;
        }
        logger.warn(`[lazyWithRetry] Retry ${i + 1}/${retries}`, { error });
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, i)));
      }
    }
    throw new Error('Unreachable');
  });
}

// Issue #3016: Phase 1m Onboarding — /onboarding route (Wave 2 Stage 2)
const Onboarding = lazyWithRetry(() => import('./pages/Onboarding'));

// Issue #1960: React.lazy によるコード分割 - 全ページコンポーネントを遅延ロード
const Login = lazyWithRetry(() => import('./pages/Login'));
const Register = lazyWithRetry(() => import('./pages/Register'));
const RegisterSuccess = lazyWithRetry(() => import('./pages/RegisterSuccess'));
const EmailVerification = lazyWithRetry(
  () => import('./pages/EmailVerification')
);
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const Customers = lazyWithRetry(() => import('./pages/Customers'));
const Reservations = lazyWithRetry(() => import('./pages/Reservations'));
const Staff = lazyWithRetry(() => import('./pages/Staff'));
const Sales = lazyWithRetry(() => import('./pages/Sales'));
const AIAnalysis = lazyWithRetry(() => import('./pages/AIAnalysis'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const AccountPage = lazyWithRetry(() => import('./pages/AccountPage'));
const Help = lazyWithRetry(() => import('./pages/Help'));
const CustomerDetail = lazyWithRetry(() => import('./pages/CustomerDetail'));
const TreatmentNew = lazyWithRetry(() => import('./pages/TreatmentNew'));
const TreatmentDetail = lazyWithRetry(() => import('./pages/TreatmentDetail'));
const MFASetup = lazyWithRetry(() => import('./pages/MFASetup'));
const PreviewLogin = lazyWithRetry(() => import('./pages/PreviewLogin'));
const SetupWizardTestPage = lazyWithRetry(() =>
  import('./pages/SetupWizardTestPage').then((m) => ({
    default: m.SetupWizardTestPage,
  }))
);
const ChurnedCustomers = lazyWithRetry(
  () => import('./pages/ChurnedCustomers')
); // Issue #2257
const FollowUpQueue = lazyWithRetry(() => import('./pages/FollowUpQueue')); // Issue #3173
const Repeat = lazyWithRetry(() => import('./pages/Repeat'));

// Issue #696: 公開予約サイト（認証不要）- 個別直接インポートでtree-shaking有効化
const BookingSiteLayout = lazyWithRetry(
  () => import('./pages/booking/BookingSiteLayout')
);
const BookingSiteHome = lazyWithRetry(
  () => import('./pages/booking/BookingSiteHome')
);
const BookingSiteStaff = lazyWithRetry(
  () => import('./pages/booking/BookingSiteStaff')
);
const BookingSiteMenu = lazyWithRetry(
  () => import('./pages/booking/BookingSiteMenu')
);
const BookingSiteDateTime = lazyWithRetry(
  () => import('./pages/booking/BookingSiteDateTime')
);
const BookingSiteConfirm = lazyWithRetry(
  () => import('./pages/booking/BookingSiteConfirm')
);
const BookingSiteComplete = lazyWithRetry(
  () => import('./pages/booking/BookingSiteComplete')
);

// Issue #2838: booking サブドメイン専用 404 ページ
const BookingNotFound = lazyWithRetry(
  () => import('./pages/booking/BookingNotFound')
);

// Issue #3049: 公開予約メール認証ページ (BookingSiteLayout 外、独立レイアウト)
const BookingSiteVerify = lazyWithRetry(
  () => import('./pages/booking/BookingSiteVerify')
);

/**
 * Issue #2838: `booking.ripia.jp/<slug>/...` 子ルートのパス → element マップ。
 *
 * `BOOKING_NESTED_ROUTE_PATHS` を `Record` のキーとして使うことで、TypeScript が
 * **すべての path に element が登録されていること** をコンパイル時に強制する。
 * これにより、子ルート追加時の SSOT (`routes/bookingRoutes.ts`) 更新忘れを防ぐ。
 */
const BOOKING_NESTED_ROUTE_ELEMENTS: Record<
  BookingNestedRoutePath,
  React.ReactNode
> = {
  '': <BookingSiteHome />, // index
  staff: <BookingSiteStaff />,
  menu: <BookingSiteMenu />,
  datetime: <BookingSiteDateTime />,
  confirm: <BookingSiteConfirm />,
  complete: <BookingSiteComplete />,
};

// 権限チェック付きルート保護コンポーネント
const RoleProtectedRoute: React.FC<{
  children: React.ReactNode;
  requiredRole?: 'owner' | 'staff';
  requiredPermission?: string;
}> = ({ children, requiredRole, requiredPermission }) => {
  const { isAuthenticated, isInitializing, user, checkPermission } =
    useAuthContext();

  // CRITICAL FIX (Issue #633): Clerk SDK初期化中は認証判定を待つ
  if (isInitializing) {
    return <PageLoadingSpinner />;
  }

  logger.debug('[RoleProtectedRoute] 権限チェック', {
    isAuthenticated,
    role: user?.role,
    requiredRole,
    requiredPermission,
  });

  // まず認証チェック
  if (!isAuthenticated) {
    logger.debug('[RoleProtectedRoute] 未認証のためログインへリダイレクト');
    return <Navigate to="/login" replace />;
  }

  // Role制御: 特定の役割が必要な場合
  if (requiredRole === 'owner' && user?.role !== 'owner') {
    logger.debug(
      '[RoleProtectedRoute] owner権限不足のためダッシュボードへリダイレクト',
      { userRole: user?.role }
    );
    return <Navigate to="/dashboard" replace />;
  }

  // Permission制御: 特定の権限が必要な場合
  if (requiredPermission && !checkPermission(requiredPermission)) {
    logger.debug(
      '[RoleProtectedRoute] 必要な権限が不足のためダッシュボードへリダイレクト',
      { requiredPermission }
    );
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// 404ハンドリング用コンポーネント（認証状態を考慮）
const NotFoundRedirect: React.FC = () => {
  const { isAuthenticated } = useAuthContext();
  const location = useLocation();
  // 外部ブックマーク / 旧メールリンクからの流入を観測するため silent redirect の前に記録
  React.useEffect(() => {
    logger.warn('[Router] Unknown path redirected', {
      path: location.pathname,
      search: location.search,
      isAuthenticated,
    });
  }, [location.pathname, location.search, isAuthenticated]);
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};

// #4038 / HAM-3471 リピート cutover（案B: owner 完全非露出）の chokepoint。
// 本番昇格: 旧 RepeatRedirectGate は preview 限定発火だったが、flag ON なら本番でも
// 発火する（`isRepeatEnabled()`）。`/` と `/dashboard` の両 route に mount し、
// owner+repeat-access を `/repeat` へ bounce ＝ owner が /dashboard に落ちる全経路を
// transitively 覆う。flag default OFF（VITE_REPEAT_ENABLED）の間は素通し＝本番無影響・
// rollback も再ビルドでこの flag を落とすだけ（build-time）。staff は owner スコープ外で素通し。
const RepeatRedirectGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  if (!isRepeatEnabled()) {
    return <>{children}</>;
  }

  return <RepeatRedirectGateInner>{children}</RepeatRedirectGateInner>;
};

const RepeatRedirectGateInner: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuthContext();
  const { isLoading: isSubscriptionLoading } = useSubscription();
  const isRepeatFirstOwner = useIsRepeatFirstOwner();

  // nav の dashboard 非表示判定（useIsRepeatFirstOwner）と同一述語を共有し、redirect 副作用を
  // 持つ gate 側でのみ timing guard（初期化中 / subscription ロード中は bounce しない）を重ねる。
  const shouldRedirect =
    isAuthenticated &&
    !isInitializing &&
    !isSubscriptionLoading &&
    isRepeatFirstOwner;

  if (shouldRedirect) {
    return <Navigate to={REPEAT_HOME_PATH} replace />;
  }

  return <>{children}</>;
};

// ログイン済みユーザーのリダイレクト用コンポーネント
// Issue #1459: Dashboard-First Architecture - Simplified
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuthContext();
  const [searchParams] = useSearchParams();

  // Issue #1184: セッション期限切れの例外処理
  const sessionExpiredReason = searchParams.get('reason');
  const isSessionExpired =
    sessionExpiredReason === 'session_expired' ||
    sessionExpiredReason === 'security';

  // CRITICAL FIX (Issue #633): Clerk SDK初期化中は認証判定を待つ
  if (isInitializing) {
    return <PageLoadingSpinner />;
  }

  // Issue #1184: セッション期限切れは常にログインフォーム表示
  if (isSessionExpired) {
    return <>{children}</>;
  }

  // Issue #1459: Dashboard-First Architecture (ADR-0014) - Phase 4 cleanup
  // 認証済み → 即ダッシュボードへ (組織・サブスクリプション不問)
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const AppRouter: React.FC = () => {
  const isPreviewEnv = import.meta.env.VITE_VERCEL_ENV === 'preview';
  const enableDevLogin = import.meta.env.VITE_ENABLE_DEV_LOGIN;
  const mode = import.meta.env.MODE;

  // Preview環境または開発環境（DEV_LOGIN有効）でのみPreviewLoginを表示
  const showPreviewLogin =
    isPreviewEnv || (mode === 'development' && enableDevLogin === 'true');

  // Issue #2838: booking.ripia.jp サブドメインでは、管理画面ルートを一切公開しない。
  // AuthenticatedRoute / Clerk 初期化フローに巻き込まれないよう、
  // slug を root 直下にマウントした別ツリーをレンダリングする。
  //
  // URL 構造: booking.ripia.jp/<slug>  (業界標準: HPB/STORES/Square)
  // 旧 URL `/booking/<slug>` は app.ripia.jp 側でのみ互換維持する。
  //
  // 子ルートは `routes/bookingRoutes.ts` の `BOOKING_NESTED_ROUTE_PATHS` を
  // SSOT として map で生成する。テスト (`AppRouter.host.test.tsx`) も同じ
  // 定数を import することで構造 drift を防止する。
  if (isBookingHost()) {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoadingSpinner />}>
          <Routes>
            <Route path="/:slug" element={<BookingSiteLayout />}>
              {BOOKING_NESTED_ROUTE_PATHS.map((path) =>
                path === '' ? (
                  <Route
                    key="__index__"
                    index
                    element={BOOKING_NESTED_ROUTE_ELEMENTS['']}
                  />
                ) : (
                  <Route
                    key={path}
                    path={path}
                    element={BOOKING_NESTED_ROUTE_ELEMENTS[path]}
                  />
                )
              )}
            </Route>
            {/* `/` や `/:slug` にマッチしない未知パスは全て BookingNotFound。
                `/<slug>/<unknown>` は BookingSiteLayout の子ルートに存在しない場合
                React Router が内部的に NotFound 扱いにするため、ここには来ない。 */}
            <Route path="*" element={<BookingNotFound />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageLoadingSpinner />}>
        <Routes>
          {/* Preview環境または開発環境専用ルート（Issue #485） */}
          {showPreviewLogin && (
            <>
              <Route path="/preview-login" element={<PreviewLogin />} />
              <Route
                path="/setup-wizard-test"
                element={<SetupWizardTestPage />}
              />
            </>
          )}

          {/* パブリックルート - 未認証時のみアクセス可能 */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <PublicRoute>
                <SignUp
                  routing="path"
                  path="/sign-up"
                  fallbackRedirectUrl="/dashboard"
                  signInUrl="/login"
                />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/register/success"
            element={
              <PublicRoute>
                <RegisterSuccess />
              </PublicRoute>
            }
          />
          <Route
            path="/email-verification/:userId"
            element={
              <PublicRoute>
                <EmailVerification />
              </PublicRoute>
            }
          />

          {/* Issue #1459: organization-creation, onboarding/*, subscription-required routes removed
          (Dashboard-First Phase 4 cleanup) */}

          {/* 🆕 Issue #3049: 公開予約メール認証 (BookingSiteLayout 外、独立レイアウト)
              verification email の magic link から遷移。stepper 不要のため layout 非経由。
              より specific な path なので booking/:slug より先に配置。 */}
          <Route path="/booking/:slug/verify" element={<BookingSiteVerify />} />

          {/* 🆕 Issue #696: 公開予約サイト（認証不要） */}
          <Route path="/booking/:slug" element={<BookingSiteLayout />}>
            <Route index element={<BookingSiteHome />} />
            <Route path="staff" element={<BookingSiteStaff />} />
            <Route path="menu" element={<BookingSiteMenu />} />
            <Route path="datetime" element={<BookingSiteDateTime />} />
            <Route path="confirm" element={<BookingSiteConfirm />} />
            <Route path="complete" element={<BookingSiteComplete />} />
          </Route>

          {/* #4094 Slice C2 (C-1 解消): #174 世代の route-level FeatureGate を除去。
              route/preview 到達は entitlement ではない (C3 four-axis — Business #194
              で Decided)。実行系の paid boundary は server-side FollowUpOperationGuard
              (Slice C1) + 実行要求時の UpgradePrompt (S11) が担う。
              route 全体は isRepeatEnabled() (build-time flag, default OFF) 内。 */}
          {isRepeatEnabled() && (
            <Route
              path="/repeat"
              element={
                <AuthenticatedRoute>
                  <RoleProtectedRoute requiredRole="owner">
                    <Repeat />
                  </RoleProtectedRoute>
                </AuthenticatedRoute>
              }
            />
          )}

          {/* 保護されたルート - 認証必須（DashboardLayout内にネスト） */}
          {/* Issue #1459: Dashboard-First Architecture - Always use AuthenticatedRoute */}
          <Route
            element={
              <AuthenticatedRoute>
                <DashboardLayout />
              </AuthenticatedRoute>
            }
          >
            <Route
              path="/"
              element={
                <RepeatRedirectGate>
                  <Navigate to="/dashboard" replace />
                </RepeatRedirectGate>
              }
            />
            {/* Issue #3016: Phase 1m Onboarding — 7 step Wizard (Wave 2 Stage 2)
                ProtectedRoute 経由でマウント。Dashboard-First 方針により強制 redirect なし。
                RegisterSuccess.tsx からの導線切替は Phase B で実施。 */}
            <Route path="/onboarding" element={<Onboarding />} />
            <Route
              path="/dashboard"
              element={
                <RepeatRedirectGate>
                  <Dashboard />
                </RepeatRedirectGate>
              }
            />
            <Route path="/customers" element={<Customers />} />
            {/* Issue #2257: 離脱顧客ページ - /customers/:id より前に配置必須 */}
            {/* Issue #3291/#3313: Free plan で route 層 FeatureGate gate (4 層 defense L2) */}
            <Route
              path="/customers/churned"
              element={
                <FeatureGate feature="churn_alert">
                  <RoleProtectedRoute requiredRole="owner">
                    <ChurnedCustomers />
                  </RoleProtectedRoute>
                </FeatureGate>
              }
            />
            {/* Issue #3173: 21 日フォローアップ Queue 3 列ページ */}
            {/* Issue #3290/#3313: Free plan で route 層 FeatureGate gate (4 層 defense L2) */}
            <Route
              path="/follow-up"
              element={
                <FeatureGate feature="follow_up">
                  <RoleProtectedRoute requiredRole="owner">
                    <FollowUpQueue />
                  </RoleProtectedRoute>
                </FeatureGate>
              }
            />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route
              path="/customers/:id/treatment/new"
              element={<TreatmentNew />}
            />
            <Route
              path="/customers/:id/treatment/:tid"
              element={<TreatmentDetail />}
            />
            <Route path="/staff" element={<Staff />} />
            <Route path="/reservations" element={<Reservations />} />
            <Route
              path="/analytics"
              element={<Navigate to="/sales" replace />}
            />
            <Route
              path="/sales"
              element={
                <RoleProtectedRoute requiredRole="owner">
                  <Sales />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/ai-analysis"
              element={
                <RoleProtectedRoute requiredRole="owner">
                  <AIAnalysis />
                </RoleProtectedRoute>
              }
            />
            {/* Issue #1913: Organization廃止 - /organization/settings → /settings?tab=shop へリダイレクト */}
            <Route
              path="/organization/settings"
              element={<Navigate to="/settings?tab=shop" replace />}
            />
            <Route
              path="/settings"
              element={
                <RoleProtectedRoute requiredRole="owner">
                  <Settings />
                </RoleProtectedRoute>
              }
            />
            {/* /account — Clerk UserProfile 埋込。
                routing="virtual" のため sub-path を Clerk が管理せず wildcard 不要。 */}
            <Route
              path="/account"
              element={
                <RoleProtectedRoute requiredRole="owner">
                  <AccountPage />
                </RoleProtectedRoute>
              }
            />
            {/* Issue #1914: 2要素認証設定ページ */}
            <Route path="/settings/security/mfa" element={<MFASetup />} />
            <Route path="/help" element={<Help />} />
          </Route>

          {/* 404 - 未認証の場合はログインへ、認証済みの場合はダッシュボードへ */}
          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
};

export default AppRouter;
